import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { forgotPasswordSchema, type ForgotPasswordInput } from "@platform/validation";
import { Button, Input, Label } from "@nthamizh/ui";
import { useForgotPassword } from "@platform/hooks";
import { Link } from "react-router";
import { MailCheck } from "lucide-react";

export function ForgotPasswordPage() {
  const forgotPassword = useForgotPassword();
  const [submitted, setSubmitted] = useState(false);
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ForgotPasswordInput>({ resolver: zodResolver(forgotPasswordSchema) });

  const onSubmit = handleSubmit(async (values) => {
    await forgotPassword.mutateAsync(values.email);
    // Always the same confirmation regardless of what actually happened
    // server-side (email exists or not, Google-auth account or not) —
    // matches the backend's own anti-enumeration response shape.
    setSubmitted(true);
  });

  if (submitted) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center px-4">
        <div className="w-full max-w-[380px] text-center">
          <div className="bg-white rounded-xl border border-gray-200 shadow-[0_1px_3px_0_rgb(0_0_0/0.06)] p-8">
            <div className="mx-auto mb-4 h-12 w-12 rounded-full bg-blue-50 flex items-center justify-center">
              <MailCheck className="h-6 w-6 text-blue-600" />
            </div>
            <h1 className="text-lg font-semibold text-gray-900">Check your email</h1>
            <p className="mt-2 text-sm text-gray-500">
              If an account exists for that email, we&apos;ve sent a link to reset your password.
            </p>
            <Link to="/login" className="mt-6 inline-block text-sm font-medium text-blue-600 hover:text-blue-700">
              Back to sign in
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center px-4">
      <div className="w-full max-w-[380px]">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center mb-4">
            <img src="/logo.png" alt="Tailseed" className="h-16 w-16 object-contain" />
          </div>
          <h1 className="text-xl font-semibold text-gray-900">Reset your password</h1>
          <p className="mt-1 text-sm text-gray-500">We&apos;ll email you a link to set a new one.</p>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 shadow-[0_1px_3px_0_rgb(0_0_0/0.06)] p-6">
          <form onSubmit={onSubmit} className="flex flex-col gap-4">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="you@example.com"
                {...register("email")}
                error={errors.email?.message}
              />
            </div>
            <Button type="submit" loading={forgotPassword.isPending} className="w-full mt-1">
              Send reset link
            </Button>
          </form>

          <p className="mt-5 text-center text-sm text-gray-500">
            <Link to="/login" className="font-medium text-blue-600 hover:text-blue-700">
              Back to sign in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
